import {Image as AntdImage, Modal, Row, Tooltip, Typography} from 'antd';
import React, {RefObject} from "react";
import SignatureCanvas from 'react-signature-canvas'
import {CheckOutlined, CloseOutlined, EditOutlined} from "@ant-design/icons";
import {connect} from "react-redux";
import IUser from "../../../model/interface/security/IUser";
import {IAppState} from "../../../redux/store";
import Utils from "../../../utils";
import LocaleText from "../../app/settings/dictionary/LocaleText";
import Button from "../button/Button";

interface IProps {
    value?: string,
    onChange?: (value?: string) => void
    user: IUser
    chosenUser?: IUser
    allowGenerating?: boolean
    waterMark?: boolean
    placeholder?: React.ReactNode
    disabled?: boolean
    suggestSaved?: boolean
}

interface IState {
    value?: string
    writing?: boolean,
    showModal?: boolean
}

class Signature extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);
        this.state = {
            value: props.value
        }
    }

    ref: RefObject<SignatureCanvas> = React.createRef<SignatureCanvas>()

    handleChange = (clear: boolean = false, generated: boolean = false, submit = false) => {
        clear && this.ref.current?.clear()
        const signature = this.ref.current?.getTrimmedCanvas().toDataURL('image/png')
        this.setState({
                value: clear ? undefined : this.ref.current?.isEmpty() && !generated ? undefined : signature,
                writing: false
            }, submit ? this.submit : undefined
        )
    }

    onBegin = () => {
        this.setState({writing: true})
    }

    showModal = () => {
        this.setState(state => ({showModal: !state.showModal}))
    }

    submit = () => {
        const {waterMark} = this.props
        if (waterMark && this.state.value) {
            // 2. Add the Watermark (Metadata)
            this.drawWaterMark()
        } else {
            this.setState({showModal: false}, this.updateParent)
        }
    }

    updateParent() {
        this.props.onChange && this.props.onChange(this.state.value)
    }

    // Add this inside your class
    drawUserName = (initials = false, submit = false) => {
        const {user, chosenUser} = this.props;
        const currentUser = (chosenUser || user)
        let userName = currentUser.employees.find(e => e.uuid === currentUser.currentEmployee)?.fullName || "Uživatel";
        if (initials) {
            userName = userName.split(' ')
                .filter(name => name)
                .map(name => name[0])
                .join('')
                .toUpperCase()
                .slice(0, 2) || '??'
        }

        const sigCanvas = this.ref.current;
        if (userName !== this.state.value) {
            this.setState({value: undefined})
            this.ref.current?.clear()
        }
        if (sigCanvas) {
            const canvas = sigCanvas.getCanvas();
            const ctx = canvas.getContext('2d');

            if (ctx) {
                // Define the font stack (Order: Mac -> Windows -> Generic)
                const fontStack = '"Snell Roundhand", "Apple Chancery", "Segoe Script", "Lucida Handwriting", cursive';

                // Scale font size to canvas height (e.g., 50% of height)
                const fontSize = Math.floor(canvas.height * 0.5);

                ctx.font = `italic ${70}px ${fontStack}`;
                ctx.fillStyle = 'black';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';

                // 2. Draw the text in the center
                ctx.fillText(userName, canvas.width / 2, canvas.height / 2);

                this.handleChange(false, true, submit)
            }
        }
    }

    drawWaterMark = () => {
        const {user, chosenUser} = this.props;
        const currentUser = (chosenUser || user);
        const sigRef = this.ref.current;

        // 1. Define the function that actually does the drawing
        const processCanvas = (sourceImage: HTMLImageElement | HTMLCanvasElement) => {
            const now = new Date().toLocaleString();
            const userName = currentUser.employees.find(e => e.uuid === currentUser.currentEmployee)?.fullName || "Uživatel";
            const watermarkText = `${userName} | ${now}`;

            const fontSize = Math.round((sourceImage.height / 150) * 36);


            const tempCtx = document.createElement('canvas').getContext('2d')
            if (tempCtx){
                tempCtx.font = `bold ${fontSize}px Arial`;
            }
            const textWidth = tempCtx?.measureText(watermarkText).width || 12;

            const finalWidth = Math.max(sourceImage.width, textWidth * 1.1);

            const verticalPadding = fontSize * 1;
            const gap = fontSize * 0.8;
            const finalHeight = sourceImage.height + fontSize + gap + (verticalPadding * 2);

            const finalCanvas = document.createElement('canvas');
            finalCanvas.width = finalWidth;
            finalCanvas.height = finalHeight;

            const ctx = finalCanvas.getContext('2d');
            if (ctx) {
                // Fill background white (optional, but good for PNG signatures)
                ctx.fillStyle = 'white';
                ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);

                // Draw the source Signature (Centered horizontally)
                const xOffset = (finalCanvas.width - sourceImage.width) / 2;
                const yOffset = verticalPadding;
                ctx.drawImage(sourceImage, xOffset, yOffset);

                // Draw Watermark
                ctx.font = `bold ${fontSize}px Arial`; // Bold makes it look more "watermark-y"
                ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'top'; // Easier to align from the top of the padding area

                // Position text below the signature
                ctx.fillText(
                    watermarkText,
                    finalCanvas.width / 2,
                    yOffset + sourceImage.height + gap
                );

                this.setState({
                    value: finalCanvas.toDataURL('image/png'),
                    showModal: false
                }, () => this.updateParent());
            }
        };

        // 2. Decide which source to use
        if (sigRef && !sigRef.isEmpty()) {
            // Use the actual signature
            const trimmed = sigRef.getTrimmedCanvas();
            processCanvas(trimmed);
        } else if (currentUser.signature) {
            // --- USE YOUR PICTURE ---
            const img = new Image();
            img.crossOrigin = "anonymous"; // Prevents "Tainted Canvas" errors if image is on a CDN
            img.src = currentUser.signature; // Put your URL here

            img.onload = () => {
                processCanvas(img);
            };
        }
    }

    render() {
        const {value, user, allowGenerating, chosenUser, disabled, placeholder, suggestSaved} = this.props
        const {writing, showModal} = this.state
        const stateValue = this.state.value
        disabled && this.ref.current?.off()

        const currentUser = (chosenUser || user)

        return (
            <div>
                <Row justify={"center"} align={'middle'} className={'position-relative border rounded p-3'}>
                    {value ?
                        <div>
                            <AntdImage preview={false} src={value} fallback={Utils.imageFallback()}
                                       style={{maxHeight: 150}}/>
                            {!disabled && (
                                <div className={'position-absolute'} style={{top: 0, right: 0}}>
                                    <Tooltip title={'Vymazat podpis'}>
                                        <Button size={"small"} type={'link'} icon={<CloseOutlined/>} danger
                                                onClick={() => this.handleChange(true, false, true)}/>
                                    </Tooltip>
                                </div>
                            )}
                        </div>
                        :
                        <div>
                            <Button disabled={disabled} type={"info"} onClick={this.showModal}
                                    icon={<EditOutlined/>}>{placeholder ||
                                <LocaleText code={'system.input.signature.sign'} fallback={'Podepsat'}/>}</Button>
                            {currentUser.currentEmployee && currentUser.signature && suggestSaved && (
                                <Button className={'ml-3'}
                                        type="dashed"
                                        onClick={() => this.setState({value: currentUser.signature}, this.submit)}
                                        style={{marginRight: 'auto'}} // Pushes it to the left
                                >
                                    <Row align={'middle'}>
                                        <LocaleText code={'system.input.signature.name'}
                                                    fallback={'Podepsat uloženým podpisem'}/>
                                        <AntdImage className={'ml-2'} height={20} src={currentUser.signature}/>
                                    </Row>
                                </Button>
                            )}
                        </div>
                    }
                </Row>
                <Modal visible={showModal} title={<LocaleText code={'system.input.signature'} fallback={'Podpis'}/>}
                       width={800}
                       onCancel={this.showModal} footer={
                    <Row justify={"space-between"} gutter={12}>

                        {currentUser.currentEmployee && allowGenerating && (
                            <div>
                                <Button
                                    size={"small"}
                                    type="dashed"
                                    onClick={() => this.drawUserName(true)}
                                    style={{marginRight: 'auto'}} // Pushes it to the left
                                >
                                    <LocaleText code={'system.input.signature.initials'} fallback={'Použít iniciály'}/>
                                </Button>
                                <Button
                                    size={"small"}
                                    type="dashed"
                                    onClick={() => this.drawUserName()}
                                    style={{marginRight: 'auto'}} // Pushes it to the left
                                >
                                    <LocaleText code={'system.input.signature.name'} fallback={'Použít jmeno'}/>
                                </Button>
                            </div>
                        )}
                        <Button size={"small"} type={'primary'} disabled={!stateValue} icon={<CheckOutlined/>}
                                onClick={this.submit}>Potvrdit</Button>
                    </Row>
                }>
                    <div className={'position-relative border rounded'} style={{height: '50vh'}}>
                        <SignatureCanvas onBegin={() => this.onBegin()} minWidth={1}
                                         onEnd={() => this.handleChange()} ref={this.ref}
                                         canvasProps={{
                                             className: 'position-absolute w-100 h-100', style: {zIndex: 2}
                                         }}/>
                        {!stateValue && !writing &&
                            <Row className={'position-absolute w-100 h-100'} justify={'center'} align={'middle'}
                                 style={{top: 0, left: 0}}>
                                <Typography.Text
                                    type={"secondary"}>{'Podepište se zde, prosím'}</Typography.Text>
                            </Row>
                        }
                        {stateValue &&
                            <div className={'position-absolute'} style={{top: 0, right: 0, zIndex: 3}}>
                                <Tooltip title={<LocaleText code={'system.input.signature.delete'}
                                                            fallback={'Vymazat podpis'}/>}>
                                    <Button size={"small"} type={'link'} icon={<CloseOutlined/>} danger
                                            onClick={() => this.handleChange(true)}/>
                                </Tooltip>
                            </div>
                        }
                    </div>
                </Modal>
            </div>
        )
    }
}

const mapStateToProps = (store: IAppState) => {
    return {
        user: store.setup.user,
    }
}

export default connect(mapStateToProps)(Signature)